<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { Primitive, type PrimitiveProps } from "reka-ui";
import { Loader } from "lucide-vue-next";

import { type ButtonVariants, buttonVariants, buttonLayout } from "./main";

interface Props extends PrimitiveProps {
  variant?: ButtonVariants["variant"];
  size?: ButtonVariants["size"];
  class?: HTMLAttributes["class"];
  loading?: boolean;
  disabled?: boolean;
  tooltipContent?: string | HTMLElement;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  tooltipClass?: string;
  onClick?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  loading: false,
  disabled: false,
  tooltipSide: "top",
  tooltipClass: "",
});

const onClick = () => {
  if (props.disabled || props.loading) {
    return;
  }
  props.onClick?.();
};
</script>

<template>
  <Tooltip v-if="tooltipContent" :delay-duration="300">
    <TooltipTrigger
      :class="classNames(buttonVariants({ variant, size, disabled }), props.class, 'cursor-pointer')"
      @click="onClick"
    >
      <div :class="classNames(buttonLayout, { 'pointer-events-none': loading || disabled })" v-bind="$attrs">
        <Loader v-if="loading" class="w-5 h-5 animate-spin" />
        <slot v-else />
      </div>
    </TooltipTrigger>
    <TooltipContent :side="tooltipSide" :class="tooltipClass">
      <div v-html="tooltipContent" />
    </TooltipContent>
  </Tooltip>

  <Primitive
    v-else
    data-slot="button"
    :as="as"
    :class="classNames(buttonVariants({ variant, size, disabled }), props.class, 'cursor-pointer')"
    :disabled="loading"
    v-bind="$attrs"
    @click="onClick"
  >
    <Loader v-if="loading" class="w-5 h-5 animate-spin" />
    <slot v-else />
  </Primitive>
</template>
